import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import resolveMediaUrl from '@/utils/resolveMediaUrl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Building2,
  Home,
  MessageSquare,
  MessageCircle,
  Plus,
  LogOut,
  Pencil,
  Trash2,
  Eye,
  LayoutDashboard,
  Menu,
  X,
  Check,
  ExternalLink,
  Settings,
  Image,
  Video,
  Upload,
  Phone,
  Mail,
  MapPin,
  Clock,
  Bot,
  Landmark,
} from 'lucide-react';
import ProjectsList from '@/components/ProjectsManagement';
import HomepageOrder from '@/components/HomepageOrder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const userData = localStorage.getItem('admin_user');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/properties', label: 'Properties', icon: Building2 },
    { path: '/admin/projects', label: 'Projects', icon: Landmark },
    { path: '/admin/homepage-order', label: 'Homepage Order', icon: LayoutDashboard },
    { path: '/admin/enquiries', label: 'Enquiries', icon: MessageSquare },
    { path: '/admin/chat-enquiries', label: 'Chat Enquiries', icon: MessageCircle },
    { path: '/admin/settings', label: 'Hero Settings', icon: Image },
    { path: '/admin/contact-settings', label: 'Contact Info', icon: Phone },
    { path: '/admin/account', label: 'Account', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  if (!localStorage.getItem('admin_token')) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#0F172A] z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <img 
            src="/images/urbancial-logo.png" 
            alt="URBANCIAL" 
            className="h-10 w-auto"
          />
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Menu className="w-6 h-6 text-white" />
          )}
        </button>
      </div>


      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-[#0F172A] z-40 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          <Link to="/" className="flex items-center justify-center">
            <img 
              src="/images/urbancial-logo.png" 
              alt="URBANCIAL" 
              className="h-12 w-auto"
            />
          </Link>
          <p className="text-[10px] text-white/60 uppercase tracking-wider text-center mt-2">
            Admin Panel
          </p>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-[#059669] text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <IconComponent className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">
                {user?.name?.charAt(0)?.toUpperCase() || 'A'}
              </span>
            </div>
            <div>
              <p className="text-white text-sm font-medium">{user?.name || 'Admin'}</p>
              <p className="text-white/60 text-xs">{user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/" target="_blank" className="flex-1">
              <Button variant="outline" size="sm" className="w-full text-xs">
                <ExternalLink className="w-3 h-3 mr-1" />
                View Site
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Routes>
            <Route path="dashboard" element={<DashboardOverview />} />
            <Route path="properties" element={<PropertiesManagement />} />
            <Route path="projects" element={<ProjectsList />} />
            <Route path="homepage-order" element={<HomepageOrder />} />
            <Route path="enquiries" element={<EnquiriesManagement />} />
            <Route path="chat-enquiries" element={<ChatEnquiriesManagement />} />
            <Route path="settings" element={<SiteSettings />} />
            <Route path="contact-settings" element={<ContactSettingsPage />} />
            <Route path="account" element={<AccountSettings />} />
            <Route path="*" element={<DashboardOverview />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

// Dashboard Overview Component
const DashboardOverview = () => {
  const [stats, setStats] = useState({
    total_properties: 0,
    total_enquiries: 0,
    unread_enquiries: 0,
    total_chat_enquiries: 0,
    unread_chat_enquiries: 0,
    total_projects: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
      }
    };
    fetchStats();
  }, []);

  return (
    <div data-testid="dashboard-overview">
      <h1 className="text-2xl font-bold text-[#0F172A] mb-8">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-white p-6 shadow-sm rounded-xl border border-[#E2E8F0]">
          <p className="text-sm text-[#94A3B8] uppercase tracking-wider mb-2">
            Total Properties
          </p>
          <p className="text-4xl font-bold text-[#0F172A]">
            {stats.total_properties}
          </p>
        </div>
        <div className="bg-white p-6 shadow-sm rounded-xl border border-[#E2E8F0]">
          <p className="text-sm text-[#94A3B8] uppercase tracking-wider mb-2">
            Builder Projects
          </p>
          <p className="text-4xl font-bold text-[#0F172A]">
            {stats.total_projects || 0}
          </p>
        </div>
        <div className="bg-white p-6 shadow-sm rounded-xl border border-[#E2E8F0]">
          <p className="text-sm text-[#94A3B8] uppercase tracking-wider mb-2">
            Form Enquiries
          </p>
          <p className="text-4xl font-bold text-[#0F172A]">
            {stats.total_enquiries}
          </p>
          {stats.unread_enquiries > 0 && (
            <p className="text-sm text-[#059669] mt-1">{stats.unread_enquiries} unread</p>
          )}
        </div>
        <div className="bg-white p-6 shadow-sm rounded-xl border border-[#E2E8F0]">
          <div className="flex items-center gap-2 mb-2">
            <Bot className="w-4 h-4 text-[#059669]" />
            <p className="text-sm text-[#94A3B8] uppercase tracking-wider">
              Chat Enquiries
            </p>
          </div>
          <p className="text-4xl font-bold text-[#0F172A]">
            {stats.total_chat_enquiries || 0}
          </p>
          {(stats.unread_chat_enquiries || 0) > 0 && (
            <p className="text-sm text-[#059669] mt-1">{stats.unread_chat_enquiries} unread</p>
          )}
        </div>
        <div className="bg-white p-6 shadow-sm rounded-xl border border-[#E2E8F0]">
          <p className="text-sm text-[#94A3B8] uppercase tracking-wider mb-2">
            Total Unread
          </p>
          <p className="text-4xl font-bold text-[#059669]">
            {(stats.unread_enquiries || 0) + (stats.unread_chat_enquiries || 0)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/admin/properties"
          className="bg-[#0F172A] p-8 text-white hover:bg-[#1E293B] transition-colors rounded-xl"
        >
          <Building2 className="w-10 h-10 text-[#10B981] mb-4" />
          <h3 className="text-xl font-bold mb-2">Manage Properties</h3>
          <p className="text-white/60">Add, edit, or remove property listings.</p>
        </Link>
        <Link
          to="/admin/enquiries"
          className="bg-[#059669] p-8 text-white hover:bg-[#047857] transition-colors rounded-xl"
        >
          <MessageSquare className="w-10 h-10 mb-4" />
          <h3 className="text-xl font-bold mb-2">Form Enquiries</h3>
          <p className="text-white/80">
            {stats.unread_enquiries} unread enquiries waiting.
          </p>
        </Link>
        <Link
          to="/admin/chat-enquiries"
          className="bg-gradient-to-r from-[#059669] to-[#10B981] p-8 text-white hover:from-[#047857] hover:to-[#059669] transition-colors rounded-xl"
        >
          <Bot className="w-10 h-10 mb-4" />
          <h3 className="text-xl font-bold mb-2">Chat Enquiries</h3>
          <p className="text-white/80">
            {stats.unread_chat_enquiries || 0} new chatbot conversations.
          </p>
        </Link>
      </div>
    </div>
  );
};

// Properties Management Component
const PropertiesManagement = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Property deleted successfully');
      fetchProperties();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingProperty(null);
    fetchProperties();
  };

  return (
    <div data-testid="properties-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-[#1A1A1A]">Properties</h1>
        <Button
          onClick={() => setShowForm(true)}
          className="btn-gold"
          data-testid="add-property-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Property
        </Button>
      </div>

      {/* Property Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProperty ? 'Edit Property' : 'Add New Property'}
            </DialogTitle>
          </DialogHeader>
          <PropertyForm property={editingProperty} onClose={handleFormClose} />
        </DialogContent>
      </Dialog>

      {/* Properties Table */}
      <div className="bg-white shadow-sm overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Property</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Listing</TableHead>
              <TableHead>Sector</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Per Sq.Ft.</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  Loading...
                </TableCell>
              </TableRow>
            ) : properties.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  No properties found
                </TableCell>
              </TableRow>
            ) : (
              properties.map((property) => (
                <TableRow key={property.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <img
                        src={property.images?.[0] || 'https://via.placeholder.com/60'}
                        alt={property.title}
                        className="w-12 h-12 object-cover"
                      />
                      <span className="font-medium line-clamp-1 max-w-[200px]">
                        {property.title}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="capitalize">
                    {property.property_type?.replace('_', ' ')}
                  </TableCell>
                  <TableCell>
                    <span className={`text-[10px] px-2 py-1 font-bold uppercase ${
                      property.listing_type === 'lease' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {property.listing_type === 'lease' ? 'Lease' : 'Sale'}
                    </span>
                  </TableCell>
                  <TableCell>{property.sector}</TableCell>
                  <TableCell>
                    {property.price_on_request
                      ? 'On Request'
                      : `₹${(property.price / 100000).toFixed(2)}L`}
                    {property.listing_type === 'lease' && !property.price_on_request && (
                      <span className="text-[10px] text-[#8A8A85]">/mo</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {property.price_on_request || !property.area_sqft
                      ? '-'
                      : `₹${Math.round(property.price / property.area_sqft).toLocaleString()}`}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {property.is_featured && (
                        <span className="text-[10px] bg-[#D4AF37] text-white px-2 py-0.5">
                          Featured
                        </span>
                      )}
                      {property.is_new_launch && (
                        <span className="text-[10px] bg-[#1A1A1A] text-white px-2 py-0.5">
                          New
                        </span>
                      )}
                      {property.is_hot_deal && (
                        <span className="text-[10px] bg-red-500 text-white px-2 py-0.5">
                          Hot
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Link to={`/property/${property.id}`} target="_blank">
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(property)}
                        data-testid={`edit-property-${property.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(property.id)}
                        className="text-red-500 hover:text-red-600"
                        data-testid={`delete-property-${property.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Property Form Component
const PropertyForm = ({ property, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadedImages, setUploadedImages] = useState(property?.images || []);
  const [videoUrl, setVideoUrl] = useState(property?.video_url || '');
  const [formData, setFormData] = useState({
    title: property?.title || '',
    description: property?.description || '',
    property_type: property?.property_type || 'office',
    listing_type: property?.listing_type || 'sale',
    sector: property?.sector || '',
    area_sqft: property?.area_sqft || '',
    price: property?.price || '',
    price_on_request: property?.price_on_request || false,
    price_unit: property?.price_unit || 'total',
    developer: property?.developer || '',
    investment_benefits: property?.investment_benefits?.join('\n') || '',
    leasing_benefits: property?.leasing_benefits?.join('\n') || '',
    amenities: property?.amenities?.join('\n') || '',
    is_featured: property?.is_featured || false,
    is_new_launch: property?.is_new_launch || false,
    is_hot_deal: property?.is_hot_deal || false,
    show_on_homepage: property?.show_on_homepage || false,
  });

  // Calculate per sq.ft. price
  const getPerSqftPrice = () => {
    if (!formData.price || !formData.area_sqft) return null;
    const price = parseFloat(formData.price);
    const area = parseInt(formData.area_sqft);
    if (formData.price_unit === 'per_sqft') {
      return price;
    }
    return Math.round(price / area);
  };

  const getTotalPrice = () => {
    if (!formData.price || !formData.area_sqft) return null;
    const price = parseFloat(formData.price);
    const area = parseInt(formData.area_sqft);
    if (formData.price_unit === 'total') {
      return price;
    }
    return price * area;
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const token = localStorage.getItem('admin_token');
    const formDataUpload = new FormData();
    
    for (let i = 0; i < files.length; i++) {
      formDataUpload.append('files', files[i]);
    }

    try {
      const response = await axios.post(`${API}/upload/images`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const newUrls = response.data.uploaded.map(item => item.url);
      setUploadedImages(prev => [...prev, ...newUrls]);
      toast.success(`${response.data.count} image(s) uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploadingImages(false);
      e.target.value = '';
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingVideo(true);
    const token = localStorage.getItem('admin_token');
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/video`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const videoFullUrl = response.data.url;
      setVideoUrl(videoFullUrl);
      toast.success('Video uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload video');
    } finally {
      setUploadingVideo(false);
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeVideo = () => {
    setVideoUrl('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...formData,
        area_sqft: parseInt(formData.area_sqft) || 0,
        price: formData.price_on_request ? null : parseFloat(formData.price) || null,
        images: uploadedImages,
        video_url: videoUrl || null,
        investment_benefits: formData.investment_benefits.split('\n').filter((i) => i.trim()),
        leasing_benefits: formData.leasing_benefits.split('\n').filter((i) => i.trim()),
        amenities: formData.amenities.split('\n').filter((i) => i.trim()),
      };

      if (property) {
        await axios.put(`${API}/properties/${property.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Property updated successfully');
      } else {
        await axios.post(`${API}/properties`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Property created successfully');
      }
      onClose();
    } catch (error) {
      toast.error('Failed to save property');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="text-sm font-medium mb-2 block">Title *</label>
          <Input
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            data-testid="property-title-input"
          />
        </div>

        <div className="md:col-span-2">
          <label className="text-sm font-medium mb-2 block">Description *</label>
          <Textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            required
            data-testid="property-description-input"
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Property Type *</label>
          <Select
            value={formData.property_type}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, property_type: value }))
            }
          >
            <SelectTrigger data-testid="property-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="office">Office Space</SelectItem>
              <SelectItem value="retail">Retail Shop</SelectItem>
              <SelectItem value="mall_shop">Mall Shop</SelectItem>
              <SelectItem value="sco">SCO Plot</SelectItem>
              <SelectItem value="commercial_plot">Commercial Plot</SelectItem>
              <SelectItem value="coworking">Co-working Space</SelectItem>
              <SelectItem value="mixed_use">Mixed Use</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Listing Type *</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, listing_type: 'sale' }))}
              className={`flex-1 py-3 px-4 text-sm font-medium border-2 transition-all ${
                formData.listing_type === 'sale'
                  ? 'border-[#D4AF37] bg-[#D4AF37] text-white'
                  : 'border-[#E5E5E0] text-[#5A5A55] hover:border-[#D4AF37]'
              }`}
              data-testid="listing-type-sale"
            >
              For Sale
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, listing_type: 'lease' }))}
              className={`flex-1 py-3 px-4 text-sm font-medium border-2 transition-all ${
                formData.listing_type === 'lease'
                  ? 'border-[#D4AF37] bg-[#D4AF37] text-white'
                  : 'border-[#E5E5E0] text-[#5A5A55] hover:border-[#D4AF37]'
              }`}
              data-testid="listing-type-lease"
            >
              For Lease
            </button>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Sector *</label>
          <Input
            name="sector"
            value={formData.sector}
            onChange={handleChange}
            placeholder="e.g., Sector 44, Golf Course Road"
            required
            data-testid="property-sector-input"
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Area (sq.ft.) *</label>
          <Input
            name="area_sqft"
            type="number"
            value={formData.area_sqft}
            onChange={handleChange}
            required
            data-testid="property-area-input"
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Developer *</label>
          <Input
            name="developer"
            value={formData.developer}
            onChange={handleChange}
            required
            data-testid="property-developer-input"
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Price (₹) {formData.listing_type === 'lease' ? '/month' : ''}</label>
          <Input
            name="price"
            type="number"
            value={formData.price}
            onChange={handleChange}
            disabled={formData.price_on_request}
            data-testid="property-price-input"
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Price Type</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, price_unit: 'total' }))}
              className={`flex-1 py-2 px-3 text-xs font-medium border transition-all ${
                formData.price_unit === 'total'
                  ? 'border-[#1A1A1A] bg-[#1A1A1A] text-white'
                  : 'border-[#E5E5E0] text-[#5A5A55]'
              }`}
              disabled={formData.price_on_request}
            >
              Total Price
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, price_unit: 'per_sqft' }))}
              className={`flex-1 py-2 px-3 text-xs font-medium border transition-all ${
                formData.price_unit === 'per_sqft'
                  ? 'border-[#1A1A1A] bg-[#1A1A1A] text-white'
                  : 'border-[#E5E5E0] text-[#5A5A55]'
              }`}
              disabled={formData.price_on_request}
            >
              Per Sq.Ft.
            </button>
          </div>
        </div>

        {/* Price Calculation Display */}
        {!formData.price_on_request && formData.price && formData.area_sqft && (
          <div className="md:col-span-2 bg-[#F5F5F0] p-4 border-l-4 border-[#D4AF37]">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-[#8A8A85] uppercase tracking-wider">Total Price</p>
                <p className="text-lg font-bold text-[#1A1A1A]">
                  ₹{getTotalPrice()?.toLocaleString('en-IN')}
                  {formData.listing_type === 'lease' && <span className="text-sm font-normal">/month</span>}
                </p>
              </div>
              <div>
                <p className="text-xs text-[#8A8A85] uppercase tracking-wider">Price Per Sq.Ft.</p>
                <p className="text-lg font-bold text-[#D4AF37]">
                  ₹{getPerSqftPrice()?.toLocaleString('en-IN')}/sq.ft.
                  {formData.listing_type === 'lease' && <span className="text-sm font-normal">/month</span>}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Switch
            checked={formData.price_on_request}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({ ...prev, price_on_request: checked }))
            }
            data-testid="price-on-request-switch"
          />
          <label className="text-sm">Price on Request</label>
        </div>

        {/* Image Upload Section */}
        <div className="md:col-span-2">
          <label className="text-sm font-medium mb-2 block">Property Images</label>
          <div className="border-2 border-dashed border-[#E5E5E0] p-6 text-center hover:border-[#D4AF37] transition-colors">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
              data-testid="image-upload-input"
            />
            <label
              htmlFor="image-upload"
              className="cursor-pointer flex flex-col items-center gap-2"
            >
              {uploadingImages ? (
                <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
              ) : (
                <>
                  <div className="w-12 h-12 bg-[#F5F5F0] rounded-full flex items-center justify-center">
                    <Plus className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <span className="text-sm text-[#5A5A55]">
                    Click to upload images (JPG, PNG, WebP)
                  </span>
                  <span className="text-xs text-[#8A8A85]">
                    You can select multiple images
                  </span>
                </>
              )}
            </label>
          </div>
          
          {/* Uploaded Images Preview */}
          {uploadedImages.length > 0 && (
            <div className="mt-4 grid grid-cols-4 gap-3">
              {uploadedImages.map((url, index) => (
                <div key={url} className="relative group">
                  <img
                    src={resolveMediaUrl(url)}
                    alt={`Property ${index + 1}`}
                    className="w-full h-20 object-cover border"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Video Upload Section */}
        <div className="md:col-span-2">
          <label className="text-sm font-medium mb-2 block">Property Video (Optional)</label>
          <div className="border-2 border-dashed border-[#E5E5E0] p-6 text-center hover:border-[#D4AF37] transition-colors">
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoUpload}
              className="hidden"
              id="video-upload"
              data-testid="video-upload-input"
            />
            <label
              htmlFor="video-upload"
              className="cursor-pointer flex flex-col items-center gap-2"
            >
              {uploadingVideo ? (
                <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
              ) : (
                <>
                  <div className="w-12 h-12 bg-[#F5F5F0] rounded-full flex items-center justify-center">
                    <Plus className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <span className="text-sm text-[#5A5A55]">
                    Click to upload video (MP4, WebM, MOV)
                  </span>
                </>
              )}
            </label>
          </div>
          
          {/* Uploaded Video Preview */}
          {videoUrl && (
            <div className="mt-4 relative">
              <video
                src={videoUrl}
                controls
                className="w-full max-h-48 bg-black"
              />
              <button
                type="button"
                onClick={removeVideo}
                className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">
            Investment Benefits (one per line)
          </label>
          <Textarea
            name="investment_benefits"
            value={formData.investment_benefits}
            onChange={handleChange}
            rows={3}
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">
            Leasing Benefits (one per line)
          </label>
          <Textarea
            name="leasing_benefits"
            value={formData.leasing_benefits}
            onChange={handleChange}
            rows={3}
          />
        </div>

        <div className="md:col-span-2">
          <label className="text-sm font-medium mb-2 block">
            Amenities (one per line)
          </label>
          <Textarea
            name="amenities"
            value={formData.amenities}
            onChange={handleChange}
            rows={3}
            placeholder="Cafeteria&#10;Gym&#10;Parking"
          />
        </div>

        {/* Status Toggles */}
        <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-[#F5F5F0]">
          <div className="flex items-center gap-3">
            <Switch
              checked={formData.is_featured}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, is_featured: checked }))
              }
              data-testid="is-featured-switch"
            />
            <label className="text-sm">Featured</label>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={formData.is_new_launch}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, is_new_launch: checked }))
              }
              data-testid="is-new-launch-switch"
            />
            <label className="text-sm">New Launch</label>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={formData.is_hot_deal}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, is_hot_deal: checked }))
              }
              data-testid="is-hot-deal-switch"
            />
            <label className="text-sm">Hot Deal</label>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={formData.show_on_homepage}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, show_on_homepage: checked }))
              }
              data-testid="show-on-homepage-switch"
            />
            <label className="text-sm">Show on Homepage</label>
          </div>
        </div>
      </div>

      <div className="flex gap-4 justify-end">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" className="btn-gold" disabled={loading} data-testid="save-property-btn">
          {loading ? 'Saving...' : property ? 'Update Property' : 'Add Property'}
        </Button>
      </div>
    </form>
  );
};

// Enquiries Management Component
const EnquiriesManagement = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEnquiries = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/enquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const handleMarkRead = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/enquiries/${id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Marked as read');
      fetchEnquiries();
    } catch (error) {
      toast.error('Failed to update enquiry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/enquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Enquiry deleted');
      fetchEnquiries();
    } catch (error) {
      toast.error('Failed to delete enquiry');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div data-testid="enquiries-management">
      <h1 className="text-2xl font-bold text-[#1A1A1A] mb-8">Enquiries</h1>

      <div className="bg-white shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Property</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Requirement</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  Loading...
                </TableCell>
              </TableRow>
            ) : enquiries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  No enquiries found
                </TableCell>
              </TableRow>
            ) : (
              enquiries.map((enquiry) => (
                <TableRow
                  key={enquiry.id}
                  className={!enquiry.is_read ? 'bg-[#D4AF37]/5' : ''}
                >
                  <TableCell className="text-sm">
                    {formatDate(enquiry.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {!enquiry.is_read && (
                        <span className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                      )}
                      <span className="font-medium">{enquiry.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p>{enquiry.phone}</p>
                      <p className="text-[#8A8A85]">{enquiry.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[200px]">
                    <p className="line-clamp-1">
                      {enquiry.property_title || 'General Enquiry'}
                    </p>
                  </TableCell>
                  <TableCell className="capitalize">
                    {enquiry.enquiry_type?.replace('_', ' ')}
                  </TableCell>
                  <TableCell className="max-w-[200px]">
                    <p className="line-clamp-2 text-sm text-[#5A5A55]">
                      {enquiry.requirement || '-'}
                    </p>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {!enquiry.is_read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkRead(enquiry.id)}
                          title="Mark as read"
                          data-testid={`mark-read-${enquiry.id}`}
                        >
                          <Check className="w-4 h-4 text-green-500" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(enquiry.id)}
                        className="text-red-500 hover:text-red-600"
                        data-testid={`delete-enquiry-${enquiry.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Chat Enquiries Management Component
const ChatEnquiriesManagement = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/chat-enquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries(response.data);
    } catch (error) {
      toast.error('Failed to fetch chat enquiries');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/chat-enquiries/${id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnquiries(
        enquiries.map((e) => (e.id === id ? { ...e, is_read: true } : e))
      );
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this chat enquiry?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/chat-enquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries(enquiries.filter((e) => e.id !== id));
      toast.success('Chat enquiry deleted');
    } catch (error) {
      toast.error('Failed to delete chat enquiry');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6" data-testid="chat-enquiries-management">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">Chat Enquiries</h2>
          <p className="text-[#64748B] text-sm mt-1">
            Enquiries received from the chatbot on your website
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-[#059669]" />
          <span className="text-sm text-[#64748B]">{enquiries.length} conversations</span>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead className="font-bold">Status</TableHead>
              <TableHead className="font-bold">Name</TableHead>
              <TableHead className="font-bold">Phone</TableHead>
              <TableHead className="font-bold">Email</TableHead>
              <TableHead className="font-bold">Requirement</TableHead>
              <TableHead className="font-bold">Date</TableHead>
              <TableHead className="font-bold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  Loading...
                </TableCell>
              </TableRow>
            ) : enquiries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-[#94A3B8]">
                  <Bot className="w-12 h-12 mx-auto mb-3 text-[#E2E8F0]" />
                  No chat enquiries yet. They will appear here when visitors use the chatbot.
                </TableCell>
              </TableRow>
            ) : (
              enquiries.map((enquiry) => (
                <TableRow
                  key={enquiry.id}
                  className={`${!enquiry.is_read ? 'bg-[#ECFDF5]' : ''} hover:bg-[#F8FAFC]`}
                >
                  <TableCell>
                    <span
                      className={`px-2 py-1 text-xs rounded-full font-medium ${
                        enquiry.is_read
                          ? 'bg-[#E2E8F0] text-[#64748B]'
                          : 'bg-[#059669] text-white'
                      }`}
                    >
                      {enquiry.is_read ? 'Read' : 'New'}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">{enquiry.name}</TableCell>
                  <TableCell>
                    <a
                      href={`tel:${enquiry.phone}`}
                      className="text-[#059669] hover:underline flex items-center gap-1"
                    >
                      <Phone className="w-3 h-3" />
                      {enquiry.phone}
                    </a>
                  </TableCell>
                  <TableCell>
                    <a
                      href={`mailto:${enquiry.email}`}
                      className="text-[#059669] hover:underline flex items-center gap-1"
                    >
                      <Mail className="w-3 h-3" />
                      {enquiry.email}
                    </a>
                  </TableCell>
                  <TableCell>
                    <Dialog>
                      <DialogTrigger asChild>
                        <button
                          className="text-left max-w-xs truncate text-[#64748B] hover:text-[#0F172A] cursor-pointer"
                          title={enquiry.requirement}
                        >
                          {enquiry.requirement.length > 40
                            ? `${enquiry.requirement.substring(0, 40)}...`
                            : enquiry.requirement}
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <Bot className="w-5 h-5 text-[#059669]" />
                            Chat Enquiry from {enquiry.name}
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <p className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1">Contact</p>
                            <p className="font-medium">{enquiry.phone}</p>
                            <p className="text-sm text-[#64748B]">{enquiry.email}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1">Requirement</p>
                            <p className="text-[#0F172A] whitespace-pre-wrap">{enquiry.requirement}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1">Received</p>
                            <p className="text-sm">{formatDate(enquiry.created_at)}</p>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </TableCell>
                  <TableCell className="text-sm text-[#64748B]">
                    {formatDate(enquiry.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      {!enquiry.is_read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markAsRead(enquiry.id)}
                          title="Mark as read"
                          className="hover:bg-[#ECFDF5] hover:text-[#059669]"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteEnquiry(enquiry.id)}
                        className="hover:bg-red-50 hover:text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Site Settings Component
const SiteSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [settings, setSettings] = useState({
    headline: 'Premium Commercial',
    headline_accent: 'Properties in Gurgaon',
    subheadline: "Discover high-value investment opportunities in India's fastest-growing commercial hub. Office spaces, retail, SCO plots & more.",
    tagline: 'Exclusive Commercial Properties',
    media_type: 'image',
    media_url: 'https://images.unsplash.com/photo-1549851501-a521e1e3fb85?w=1920',
    button_primary_text: 'Explore Properties',
    button_secondary_text: 'Schedule Consultation',
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/hero`);
        setSettings(response.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleMediaUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingMedia(true);
    const token = localStorage.getItem('admin_token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = type === 'video' ? '/upload/video' : '/upload/image';
      const response = await axios.post(`${API}${endpoint}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const mediaUrl = response.data.url;
      setSettings(prev => ({
        ...prev,
        media_url: mediaUrl,
        media_type: type,
      }));
      toast.success(`${type === 'video' ? 'Video' : 'Image'} uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload media');
    } finally {
      setUploadingMedia(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/settings/hero`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="site-settings">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Site Settings</h1>
          <p className="text-sm text-[#5A5A55] mt-1">Manage your homepage hero section</p>
        </div>
        <Button
          onClick={handleSave}
          className="btn-gold"
          disabled={saving}
          data-testid="save-settings-btn"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left: Form Fields */}
        <div className="bg-white p-8 shadow-sm space-y-6">
          <h2 className="text-lg font-bold text-[#1A1A1A] border-b pb-4 mb-6">Hero Content</h2>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Tagline (small text above headline)</label>
            <Input
              name="tagline"
              value={settings.tagline}
              onChange={handleChange}
              placeholder="e.g., Exclusive Commercial Properties"
              data-testid="hero-tagline-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Main Headline</label>
            <Input
              name="headline"
              value={settings.headline}
              onChange={handleChange}
              placeholder="e.g., Premium Commercial"
              data-testid="hero-headline-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Headline Accent (blue text)</label>
            <Input
              name="headline_accent"
              value={settings.headline_accent}
              onChange={handleChange}
              placeholder="e.g., Properties in Gurgaon"
              data-testid="hero-headline-accent-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Subheadline</label>
            <Textarea
              name="subheadline"
              value={settings.subheadline}
              onChange={handleChange}
              rows={3}
              placeholder="Describe your business..."
              data-testid="hero-subheadline-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Primary Button Text</label>
              <Input
                name="button_primary_text"
                value={settings.button_primary_text}
                onChange={handleChange}
                placeholder="e.g., Explore Properties"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Secondary Button Text</label>
              <Input
                name="button_secondary_text"
                value={settings.button_secondary_text}
                onChange={handleChange}
                placeholder="e.g., Schedule Consultation"
              />
            </div>
          </div>
        </div>

        {/* Right: Media Upload */}
        <div className="bg-white p-8 shadow-sm">
          <h2 className="text-lg font-bold text-[#1A1A1A] border-b pb-4 mb-6">Hero Background Media</h2>
          
          {/* Media Type Toggle */}
          <div className="flex gap-4 mb-6">
            <button
              type="button"
              onClick={() => setSettings(prev => ({ ...prev, media_type: 'image' }))}
              className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 border-2 transition-all ${
                settings.media_type === 'image'
                  ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#1A1A1A]'
                  : 'border-[#E5E5E0] text-[#5A5A55]'
              }`}
              data-testid="media-type-image"
            >
              <Image className="w-5 h-5" />
              Image
            </button>
            <button
              type="button"
              onClick={() => setSettings(prev => ({ ...prev, media_type: 'video' }))}
              className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 border-2 transition-all ${
                settings.media_type === 'video'
                  ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#1A1A1A]'
                  : 'border-[#E5E5E0] text-[#5A5A55]'
              }`}
              data-testid="media-type-video"
            >
              <Video className="w-5 h-5" />
              Video
            </button>
          </div>

          {/* Upload Area */}
          <div className="border-2 border-dashed border-[#E5E5E0] p-8 text-center hover:border-[#D4AF37] transition-colors mb-6">
            <input
              type="file"
              accept={settings.media_type === 'video' ? 'video/*' : 'image/*'}
              onChange={(e) => handleMediaUpload(e, settings.media_type)}
              className="hidden"
              id="hero-media-upload"
              data-testid="hero-media-upload"
            />
            <label htmlFor="hero-media-upload" className="cursor-pointer flex flex-col items-center gap-3">
              {uploadingMedia ? (
                <div className="animate-spin w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
              ) : (
                <>
                  <div className="w-16 h-16 bg-[#F5F5F0] rounded-full flex items-center justify-center">
                    <Upload className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <span className="text-sm text-[#5A5A55]">
                    Click to upload {settings.media_type === 'video' ? 'video' : 'image'}
                  </span>
                  <span className="text-xs text-[#8A8A85]">
                    {settings.media_type === 'video' ? 'MP4, WebM (MOV not recommended for web browsers)' : 'JPG, PNG, WebP'}
                  </span>
                  {settings.media_type === 'video' && (
                    <span className="text-xs text-amber-600 font-medium mt-1 block">
                      ⚠️ For best compatibility, please upload MP4 format videos
                    </span>
                  )}
                </>
              )}
            </label>
          </div>

          {/* Current Media Preview */}
          <div>
            <label className="text-sm font-medium mb-2 block">Current Background</label>
            {settings.media_type === 'video' ? (
              <video
                src={resolveMediaUrl(settings.media_url)}
                controls
                className="w-full h-48 object-cover bg-black"
              />
            ) : (
              <div
                className="w-full h-48 bg-cover bg-center border"
                style={{ backgroundImage: `url(${resolveMediaUrl(settings.media_url)})` }}
              />
            )}
            
            {/* URL Input as fallback */}
            <div className="mt-4">
              <label className="text-xs text-[#8A8A85] mb-1 block">Or enter URL directly:</label>
              <Input
                name="media_url"
                value={settings.media_url}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
                className="text-sm"
                data-testid="hero-media-url-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      <div className="mt-8 bg-white p-8 shadow-sm">
        <h2 className="text-lg font-bold text-[#1A1A1A] border-b pb-4 mb-6">Live Preview</h2>
        <div className="relative h-72 overflow-hidden rounded-lg">
          {settings.media_type === 'video' ? (
            <video
              src={resolveMediaUrl(settings.media_url)}
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${resolveMediaUrl(settings.media_url)})` }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/50 via-[#0F172A]/60 to-[#0F172A]/80" />
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center p-6">
            <p className="text-[#10B981] text-xs uppercase tracking-[0.3em] mb-2 font-semibold">
              {settings.tagline}
            </p>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {settings.headline}
              <br />
              <span className="text-[#10B981]">{settings.headline_accent}</span>
            </h1>
            <p className="text-white/80 text-sm max-w-lg mb-4">
              {settings.subheadline}
            </p>
            <div className="flex items-center gap-3">
              <span className="bg-[#059669] text-white px-5 py-2.5 text-xs font-bold uppercase rounded">
                {settings.button_primary_text}
              </span>
              <span className="border border-white/50 text-white px-5 py-2.5 text-xs font-bold uppercase rounded">
                {settings.button_secondary_text}
              </span>
            </div>
          </div>
        </div>
        <p className="text-xs text-[#8A8A85] mt-3">* The actual website also includes a search bar in the hero section.</p>
      </div>
    </div>
  );
};

// Contact Settings Page Component
const ContactSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    phone_primary: '+91 99999 99999',
    phone_secondary: '',
    email_primary: 'info@urbancial.com',
    email_secondary: '',
    whatsapp_number: '+919999999999',
    address_line1: 'Golf Course Road, Sector 54',
    address_line2: 'Gurugram, Haryana 122002',
    working_hours_weekday: 'Mon - Sat: 10:00 AM - 7:00 PM',
    working_hours_weekend: 'Sunday: Closed',
    google_maps_url: '',
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    twitter_url: '',
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/contact`);
        setSettings(response.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/settings/contact`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Contact settings saved successfully');
    } catch (error) {
      toast.error('Failed to save contact settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="contact-settings">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Contact Information</h1>
          <p className="text-sm text-[#5A5A55] mt-1">Manage contact details shown across the website</p>
        </div>
        <Button
          onClick={handleSave}
          className="btn-gold"
          disabled={saving}
          data-testid="save-contact-settings-btn"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Phone Numbers */}
        <div className="bg-white p-8 shadow-sm space-y-6">
          <div className="flex items-center gap-3 border-b pb-4 mb-6">
            <div className="w-10 h-10 bg-[#D4AF37] flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-bold text-[#1A1A1A]">Phone Numbers</h2>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Primary Phone *</label>
            <Input
              name="phone_primary"
              value={settings.phone_primary}
              onChange={handleChange}
              placeholder="+91 99999 99999"
              data-testid="phone-primary-input"
            />
            <p className="text-xs text-[#8A8A85] mt-1">Shown in header, footer, and contact page</p>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Secondary Phone (Optional)</label>
            <Input
              name="phone_secondary"
              value={settings.phone_secondary}
              onChange={handleChange}
              placeholder="+91 88888 88888"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">WhatsApp Number</label>
            <Input
              name="whatsapp_number"
              value={settings.whatsapp_number}
              onChange={handleChange}
              placeholder="+919999999999 (without spaces)"
              data-testid="whatsapp-input"
            />
            <p className="text-xs text-[#8A8A85] mt-1">Format: +919999999999 (with country code, no spaces)</p>
          </div>
        </div>

        {/* Email Addresses */}
        <div className="bg-white p-8 shadow-sm space-y-6">
          <div className="flex items-center gap-3 border-b pb-4 mb-6">
            <div className="w-10 h-10 bg-[#D4AF37] flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-bold text-[#1A1A1A]">Email Addresses</h2>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Primary Email *</label>
            <Input
              name="email_primary"
              type="email"
              value={settings.email_primary}
              onChange={handleChange}
              placeholder="info@urbancial.com"
              data-testid="email-primary-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Secondary Email (Optional)</label>
            <Input
              name="email_secondary"
              type="email"
              value={settings.email_secondary}
              onChange={handleChange}
              placeholder="sales@urbancial.com"
            />
          </div>
        </div>

        {/* Address */}
        <div className="bg-white p-8 shadow-sm space-y-6">
          <div className="flex items-center gap-3 border-b pb-4 mb-6">
            <div className="w-10 h-10 bg-[#D4AF37] flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-bold text-[#1A1A1A]">Office Address</h2>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Address Line 1 *</label>
            <Input
              name="address_line1"
              value={settings.address_line1}
              onChange={handleChange}
              placeholder="Golf Course Road, Sector 54"
              data-testid="address-line1-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Address Line 2</label>
            <Input
              name="address_line2"
              value={settings.address_line2}
              onChange={handleChange}
              placeholder="Gurugram, Haryana 122002"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Google Maps URL (Optional)</label>
            <Input
              name="google_maps_url"
              value={settings.google_maps_url}
              onChange={handleChange}
              placeholder="https://maps.google.com/..."
            />
          </div>
        </div>

        {/* Working Hours */}
        <div className="bg-white p-8 shadow-sm space-y-6">
          <div className="flex items-center gap-3 border-b pb-4 mb-6">
            <div className="w-10 h-10 bg-[#D4AF37] flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-bold text-[#1A1A1A]">Working Hours</h2>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Weekday Hours</label>
            <Input
              name="working_hours_weekday"
              value={settings.working_hours_weekday}
              onChange={handleChange}
              placeholder="Mon - Sat: 10:00 AM - 7:00 PM"
              data-testid="working-hours-weekday-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Weekend Hours</label>
            <Input
              name="working_hours_weekend"
              value={settings.working_hours_weekend}
              onChange={handleChange}
              placeholder="Sunday: Closed"
            />
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white p-8 shadow-sm space-y-6 lg:col-span-2">
          <h2 className="text-lg font-bold text-[#1A1A1A] border-b pb-4 mb-6">Social Media Links (Optional)</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium mb-2 block">Facebook URL</label>
              <Input
                name="facebook_url"
                value={settings.facebook_url}
                onChange={handleChange}
                placeholder="https://facebook.com/urbancial"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Instagram URL</label>
              <Input
                name="instagram_url"
                value={settings.instagram_url}
                onChange={handleChange}
                placeholder="https://instagram.com/urbancial"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">LinkedIn URL</label>
              <Input
                name="linkedin_url"
                value={settings.linkedin_url}
                onChange={handleChange}
                placeholder="https://linkedin.com/company/urbancial"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Twitter URL</label>
              <Input
                name="twitter_url"
                value={settings.twitter_url}
                onChange={handleChange}
                placeholder="https://twitter.com/urbancial"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Preview Card */}
      <div className="mt-8 bg-white p-8 shadow-sm">
        <h2 className="text-lg font-bold text-[#1A1A1A] border-b pb-4 mb-6">Preview - How It Will Appear</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-[#F5F5F0]">
            <div className="flex items-center gap-2 mb-2">
              <Phone className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-medium">Phone</span>
            </div>
            <p className="text-[#1A1A1A]">{settings.phone_primary}</p>
            {settings.phone_secondary && <p className="text-[#5A5A55] text-sm">{settings.phone_secondary}</p>}
          </div>
          <div className="p-4 bg-[#F5F5F0]">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-medium">Email</span>
            </div>
            <p className="text-[#1A1A1A]">{settings.email_primary}</p>
            {settings.email_secondary && <p className="text-[#5A5A55] text-sm">{settings.email_secondary}</p>}
          </div>
          <div className="p-4 bg-[#F5F5F0]">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-medium">Hours</span>
            </div>
            <p className="text-[#1A1A1A]">{settings.working_hours_weekday}</p>
            <p className="text-[#5A5A55] text-sm">{settings.working_hours_weekend}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== ACCOUNT SETTINGS ==========
const AccountSettings = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setAdminEmail(res.data.email);
      } catch (err) {}
    };
    fetchMe();
  }, []);

  const handleSave = async () => {
    if (!currentPassword) {
      toast.error('Current password is required');
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword && newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!newEmail && !newPassword) {
      toast.error('Enter a new email or password to update');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const payload = { current_password: currentPassword };
      if (newEmail) payload.new_email = newEmail;
      if (newPassword) payload.new_password = newPassword;
      await axios.put(`${API}/auth/account`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Account updated successfully');
      if (newEmail) setAdminEmail(newEmail);
      setCurrentPassword('');
      setNewEmail('');
      setNewPassword('');
      setConfirmPassword('');
      if (newPassword) {
        toast.info('Password changed. Please login again.');
        localStorage.removeItem('admin_token');
        window.location.href = '/admin/login';
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="account-settings">
      <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">Account Settings</h1>
      <p className="text-sm text-[#8A8A85] mb-8">Change your login email and password</p>

      <div className="max-w-lg">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-[#E2E8F0]">
          <div className="mb-6 p-4 rounded-lg" style={{ background: '#F5F5F0' }}>
            <p className="text-xs text-[#8A8A85] mb-1">Current Email</p>
            <p className="text-[#1A1A1A] font-semibold" data-testid="current-email">{adminEmail}</p>
          </div>

          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium mb-2 block text-[#1A1A1A]">Current Password *</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37]"
                style={{ borderColor: '#E2E8F0' }}
                placeholder="Enter current password to confirm"
                data-testid="current-password-input"
              />
            </div>

            <hr style={{ borderColor: 'rgba(201,168,76,0.15)' }} />

            <div>
              <label className="text-sm font-medium mb-2 block text-[#1A1A1A]">New Email (optional)</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37]"
                style={{ borderColor: '#E2E8F0' }}
                placeholder="Enter new email"
                data-testid="new-email-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block text-[#1A1A1A]">New Password (optional)</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37]"
                style={{ borderColor: '#E2E8F0' }}
                placeholder="Enter new password (min 6 chars)"
                data-testid="new-password-input"
              />
            </div>

            {newPassword && (
              <div>
                <label className="text-sm font-medium mb-2 block text-[#1A1A1A]">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37]"
                  style={{ borderColor: '#E2E8F0' }}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full py-3 rounded-lg text-white text-sm font-bold uppercase tracking-wider transition-all duration-300"
              style={{ background: 'linear-gradient(135deg, #8a6a10, #c9a84c)' }}
              data-testid="save-account-btn"
            >
              {saving ? 'Saving...' : 'Update Account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default AdminDashboard;
